import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Invitation {
  token: string;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  created_at: string;
  status: string;
}

export default function InvitationsPage() {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ email: '', role: 'manager', first_name: '', last_name: '' });
  const [copiedToken, setCopiedToken] = useState('');

  const fetchInvitations = async () => {
    try {
      const data = await api.get('/admin/invitations');
      setInvitations(data.items || []);
    } catch {
      setError('Урилгын жагсаалт ачаалахад алдаа');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvitations(); }, []);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) { setError('И-мэйл хаяг оруулна уу'); return; }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.post('/admin/invitations', form);
      setSuccess(`Урилга илгээлээ: ${data.demo_link}`);
      setForm({ email: '', role: 'manager', first_name: '', last_name: '' });
      setShowForm(false);
      fetchInvitations();
    } catch (err: any) {
      const detail = err?.body ? JSON.parse(err.body)?.detail : null;
      setError(detail || 'Урилга илгээхэд алдаа');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (token: string) => {
    const link = `${window.location.origin}/register/accept?token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(''), 2000);
  };

  const roleLabel: Record<string, string> = {
    manager: 'Менежер',
    director: 'Захирал',
    accountant: 'Нягтлан',
    platform_admin: 'Платформ админ',
  };

  const roleBadge = (role: string) => {
    const colors: Record<string, string> = {
      director: '#7c3aed', manager: '#2563eb', accountant: '#059669', platform_admin: '#dc2626',
    };
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 6,
        fontSize: 12, fontWeight: 600,
        background: `${colors[role] || '#6b7280'}18`, color: colors[role] || '#6b7280',
      }}>
        {roleLabel[role] || role}
      </span>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Inter'", fontSize: 24, fontWeight: 700, color: '#1c2b22', margin: 0 }}>
            Урилга удирдлага
          </h1>
          <p style={{ color: '#5f6f66', fontSize: 14, marginTop: 4 }}>
            Шинэ админ хэрэглэгч урих, урилгын төлөв хянах
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>
          {showForm ? 'Хаах' : '+ Шинэ урилга'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#f0fdf4', color: '#166534', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {success}
        </div>
      )}

      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: "'Inter'", fontSize: 16, fontWeight: 600, color: '#1c2b22', marginTop: 0, marginBottom: 16 }}>
            Шинэ урилга илгээх
          </h3>
          <form onSubmit={handleSend}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>И-мэйл *</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  style={inputStyle} placeholder="admin@sokh.mn" type="email" required />
              </div>
              <div>
                <label style={labelStyle}>Эрх</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inputStyle}>
                  <option value="manager">Менежер</option>
                  <option value="director">Захирал</option>
                  <option value="accountant">Нягтлан</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Нэр</label>
                <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
                  style={inputStyle} placeholder="Бат" />
              </div>
              <div>
                <label style={labelStyle}>Овог</label>
                <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
                  style={inputStyle} placeholder="Дорж" />
              </div>
            </div>
            <button type="submit" disabled={sending} style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }}>
              {sending ? 'Илгээж байна...' : 'Урилга илгээх'}
            </button>
          </form>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5f6f66' }}>Ачаалж байна...</div>
        ) : invitations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5f6f66' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📨</div>
            <p style={{ margin: 0 }}>Одоогоор урилга байхгүй</p>
            <p style={{ margin: '8px 0 0', fontSize: 13 }}>Шинэ админ хэрэглэгч урихын тулд дээрх товчлуурыг дарна уу</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#faf9f5', borderBottom: '1px solid #e8e5dc' }}>
                <th style={thStyle}>И-мэйл</th>
                <th style={thStyle}>Нэр</th>
                <th style={thStyle}>Эрх</th>
                <th style={thStyle}>Огноо</th>
                <th style={thStyle}>Төлөв</th>
                <th style={thStyle}>Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.token} style={{ borderBottom: '1px solid #f0ede4' }}>
                  <td style={tdStyle}>{inv.email}</td>
                  <td style={tdStyle}>{inv.first_name} {inv.last_name}</td>
                  <td style={tdStyle}>{roleBadge(inv.role)}</td>
                  <td style={tdStyle}>{new Date(inv.created_at).toLocaleDateString('mn-MN')}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                      fontSize: 12, fontWeight: 600,
                      background: '#fef3c7', color: '#92400e',
                    }}>
                      Хүлээгдэж байна
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => copyLink(inv.token)} style={linkBtn}>
                      {copiedToken === inv.token ? '✓ Хуулсан' : 'Линк хуулах'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
  padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 6, fontSize: 13, color: '#2a3b32', fontWeight: 500 };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #dcdacf', borderRadius: 10,
  fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6',
};
const thStyle: React.CSSProperties = { padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#5f6f66' };
const tdStyle: React.CSSProperties = { padding: '12px 16px', fontSize: 14, color: '#1c2b22' };
const linkBtn: React.CSSProperties = {
  background: 'none', border: '1px solid #dcdacf', padding: '5px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#236844', fontWeight: 600,
};
